import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Loader, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Charts } from '@/_shared/components/Charts';
import { ReadMoreTypography } from '@/_shared/components/ReadMoreTypography';
import { Surveys } from '@/_shared/components/Surveys';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  getSurveyCampaignsResultsStatsCall,
  getTemplatesCall,
} from '@/surveys/state';
import { ResultsNotVisible } from '../../ResultsNotVisible';
import { DistributionBox } from './DistributionBox';
import { EmptyState } from './EmptyState';
import { SurveyDepartmentResultsVsOrganization } from './SurveyDepartmentResultsVsOrganization';

type Props = {
  departmentId?: string;
  departmentName?: string;
  activeChip: string;
};

export const AssessmentByTemplate: FC<Props> = ({ departmentId, departmentName, activeChip }) => {
  const t = useTranslations('department.details.assessment');
  const tShared = useTranslations();

  const { genericResponsiveValue } = useResponsiveStyle();

  const gapXs = useThemeValue('space', 'xs');

  const {
    execute: executeGetSurveyCampaignByIdResponsesResultsStats,
    loading: loadingGetSurveyCampaignByIdResponsesResultsStats,
    error: errorGetSurveyCampaignByIdResponsesResultsStats,
    success: successGetSurveyCampaignByIdResponsesResultsStats,
    departmentsStats,
    conElementsDepartmentStats,
    proElementsDepartmentStats,
  } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { response: responseGetSurveyCampaignsResultsStats } =
    getSurveyCampaignsResultsStatsCall.hook();

  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const currentTemplateId = useMemo(() => {
    const templateId = responseGetTemplates?.find(template => template.name === activeChip)?.id;
    return templateId;
  }, [activeChip, responseGetTemplates]);

  const currentCampaignId = useMemo(() => {
    const campaign = responseGetSurveyCampaignsResultsStats?.findLast(
      currentCampaign => currentCampaign.surveyTemplateId === currentTemplateId,
    );
    return campaign?.campaignId;
  }, [currentTemplateId, responseGetSurveyCampaignsResultsStats]);

  const nameSurvey = useMemo(() => {
    if (responseGetTemplates && responseGetSurveyCampaignsResultsStats) {
      const templateName = responseGetTemplates.find(
        template => template?.id === currentTemplateId,
      );
      return templateName?.name || '';
    }
    return '';
  }, [responseGetTemplates, responseGetSurveyCampaignsResultsStats, currentTemplateId]);

  const totalCompletedSurveys = useMemo(() => {
    return departmentsStats?.[0]?.totalCompletedSurveys || 0;
  }, [departmentsStats]);

  const totalInvited = useMemo(() => {
    return departmentsStats?.[0]?.totalInvited || 0;
  }, [departmentsStats]);

  const notInvited =
    departmentsStats?.[0].totalCompletedSurveys === 0 && departmentsStats?.[0].totalInvited === 0;

  const getDescriptionMetric = useCallback(
    (surveyTemplateId: string, metricName: string) => {
      if (!responseGetTemplates) {
        return '';
      }
      return (
        responseGetTemplates
          ?.find(template => template.id === surveyTemplateId)
          ?.metrics?.find(metric => metric.name === metricName)?.description ?? ''
      );
    },
    [responseGetTemplates],
  );

  useEffect(() => {
    if (departmentId && currentCampaignId) {
      executeGetSurveyCampaignByIdResponsesResultsStats({
        campaignId: currentCampaignId,
        departmentId,
      });
    }
  }, [currentCampaignId, departmentId, executeGetSurveyCampaignByIdResponsesResultsStats]);

  if (loadingGetSurveyCampaignByIdResponsesResultsStats) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetSurveyCampaignByIdResponsesResultsStats) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (currentCampaignId === undefined) {
    return <EmptyState />;
  }

  if (
    successGetSurveyCampaignByIdResponsesResultsStats &&
    !loadingGetSurveyCampaignByIdResponsesResultsStats &&
    !errorGetSurveyCampaignByIdResponsesResultsStats
  ) {
    return (
      <Box>
        {departmentsStats?.length === 0 || notInvited ? (
          <EmptyState />
        ) : (
          <Box mt='m'>
            <Box>
              {totalCompletedSurveys > 0 &&
                departmentsStats &&
                departmentsStats[0].canViewResults && (
                  <>
                    <Typography variant='h3' mt='xxs' mb='xxs'>
                      {nameSurvey}
                    </Typography>
                    <Typography variant='p3/light' mb='m'>
                      {t(
                        totalCompletedSurveys === 1
                          ? 'intro.description.single'
                          : 'intro.description.plural',
                        {
                          departmentName,
                          nameSurvey,
                          totalCompletedSurveys,
                        },
                      )}
                    </Typography>
                  </>
                )}
              <Surveys.RecapAssessment
                invitedTotal={totalInvited || 0}
                completedTotal={totalCompletedSurveys || 0}
                templateName={nameSurvey}
              />
              {totalCompletedSurveys > 0 &&
                departmentsStats &&
                departmentsStats[0].canViewResults && (
                  <>
                    <Box
                      mt='l'
                      display='flex'
                      flexDirection={genericResponsiveValue({
                        breakPoints: { desktop: 'row' },
                        defaultValue: 'column',
                      })}
                      alignItems='flex-start'
                      flex={10}
                      style={{ gap: gapXs }}
                    >
                      <Box flex='2'>
                        <Box>
                          <Typography mb='xs' variant='h3'>
                            {t('genericSurveyResults.genericAverageGraphTitle', {
                              surveyTemplateName: activeChip,
                            })}
                          </Typography>
                        </Box>
                        <Typography mb='m' variant='p3/light'>
                          {t('genericSurveyResults.description', {
                            metricsCount:
                              (departmentsStats &&
                                departmentsStats[0].metricsScore &&
                                departmentsStats?.[0]?.metricsScore.length) ||
                              0,
                            surveyTemplateName: activeChip,
                          })}
                        </Typography>
                        <Charts.RadarChartComponent
                          data={(departmentsStats?.[0]?.metricsScore ?? []).map(metric => {
                            return {
                              subject: metric.metric?.name ?? '',
                              current: metric.score ?? 0,
                              expected: 7,
                              description: getDescriptionMetric(
                                currentTemplateId || '',
                                metric.metric?.name || '',
                              ),
                            };
                          })}
                        />
                      </Box>
                      <Box flex='1'>
                        <Card pl='s'>
                          <Typography variant='h4'>
                            {t('genericSurveyResults.insightBox.title')}
                          </Typography>
                          <Box display='flex' flexDirection='column' mb='m' mt='xs'>
                            <ReadMoreTypography variant='p3/light'>
                              {departmentsStats?.[0]?.insight}
                            </ReadMoreTypography>
                          </Box>
                          <Typography variant='h4'>
                            {t('genericSurveyResults.insightBox.proTitle')}
                          </Typography>
                          <Box display='flex' flexDirection='column' mb='m' mt='xs'>
                            {proElementsDepartmentStats?.map(item => (
                              <Card
                                mb='xxs'
                                backgroundColor='primary/inactive'
                                key={item.metric?.name}
                                px='xs'
                                py='xxxs'
                                width='fit-content'
                              >
                                <Typography variant='p2/light' fontSize='xs'>
                                  {item.metric?.name}
                                </Typography>
                              </Card>
                            ))}
                          </Box>
                          <Typography variant='h4'>
                            {t('genericSurveyResults.insightBox.conTitle')}
                          </Typography>
                          <Box display='flex' flexDirection='column' mt='xs'>
                            {conElementsDepartmentStats?.map(item => (
                              <Card
                                mb='xxs'
                                backgroundColor='error/opacity'
                                key={item.metric?.name}
                                px='xs'
                                py='xxxs'
                                width='fit-content'
                              >
                                <Typography variant='p2/light' fontSize='xs'>
                                  {item.metric?.name}
                                </Typography>
                              </Card>
                            ))}
                          </Box>
                        </Card>
                      </Box>
                    </Box>
                    <Box mt='l'>
                      <SurveyDepartmentResultsVsOrganization />
                    </Box>
                    <Box mt='l'>
                      <DistributionBox />
                    </Box>
                  </>
                )}

              {totalCompletedSurveys > 0 && !departmentsStats?.[0].canViewResults && (
                <ResultsNotVisible templateName={nameSurvey} />
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return null;
};
